import React from 'react';
import { Link } from 'react-router-dom';
import CompactShareButton from './CompactShareButton';
import { formatEngagementCount } from '../utils/contentEngagement';
import './NewsCard.css';

/**
 * NewsCard Component
 * Displays a news item as a card with image, title, description, and metadata
 * Supports multiple variants: default, featured, trending, related
 */
function NewsCard({ news, article, variant = 'default' }) {
  // Support both 'news' and 'article' prop names for backwards compatibility
  const newsItem = news || article;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('hi-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text, length) => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  if (variant === 'featured') {
    return (
      <Link to={`/news/${newsItem.slug || newsItem.id}`} className="news-card featured-card">
        <div className="featured-image-container">
          <img 
            src={newsItem.thumbnail || newsItem.image} 
            alt={newsItem.title}
            className="featured-image"
            loading="lazy"
          />
          {newsItem.isBreaking && <span className="breaking-badge">ब्रेकिंग</span>}
        </div>
        <div className="featured-content">
          <h2 className="featured-title">{newsItem.title}</h2>
          <p className="featured-description">
            {truncateText(newsItem.shortDescription || newsItem.description, 200)}
          </p>
          <div className="featured-meta">
            <span className="category-tag">{newsItem.categoryName || newsItem.category}</span>
            <span className="publish-date">📅 {formatDate(newsItem.createdAt || newsItem.date)}</span>
            <span className="publish-date">👁️ {formatEngagementCount(newsItem.views)} views</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'trending') {
    return (
      <Link to={`/news/${newsItem.slug || newsItem.id}`} className="news-card trending-card">
        <div className="trending-image">
          <img 
            src={newsItem.thumbnail || newsItem.image} 
            alt={newsItem.title}
            loading="lazy"
          />
          <span className="views-badge">👁️ {formatEngagementCount(newsItem.views)} </span>
        </div>
        <div className="trending-content">
          <h3 className="trending-title">{truncateText(newsItem.title, 50)}</h3>
          <p className="trending-date">{formatDate(newsItem.createdAt || newsItem.date)}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'related') {
    return (
      <Link to={`/news/${newsItem.slug || newsItem.id}`} className="news-card related-card">
        <div className="related-image">
          <img 
            src={newsItem.thumbnail || newsItem.image} 
            alt={newsItem.title}
            loading="lazy"
          />
        </div>
        <div className="related-content">
          <h4 className="related-title">{truncateText(newsItem.title, 40)}</h4>
        </div>
      </Link>
    );
  }

  // Default variant
  return (
    <article className="news-card card" aria-label={newsItem.title}>
      <div className="news-card-image">
        <img
          src={newsItem.thumbnail || newsItem.image}
          alt={newsItem.title}
          loading="lazy"
          onLoad={(e) => e.target.classList.add('loaded')}
        />
        <div className="news-card-badge">{newsItem.categoryName || newsItem.category}</div>
        {newsItem.isBreaking && <div className="news-card-featured">⭐ ब्रेकिंग</div>}
        {newsItem.featured && <div className="news-card-featured">⭐ फीचर्ड</div>}
      </div>

      <div className="news-card-content">
        <div className="news-card-meta">
          {newsItem.district && <span className="location">📍 {newsItem.district}</span>}
          <span className="news-date">📅 {formatDate(newsItem.createdAt || newsItem.date)}</span>
        </div>

        <h3 className="news-card-title">
          <Link to={`/news/${newsItem.slug || newsItem.id}`}>{newsItem.title}</Link>
        </h3>

        <p className="news-card-description">
          {truncateText(newsItem.shortDescription || newsItem.description, 100)}
        </p>

        <div className="news-card-footer">
          <span className="news-views">👁️ {formatEngagementCount(newsItem.views)} views</span>
          <CompactShareButton
            title={newsItem.title}
            url={`/news/${newsItem.slug || newsItem.id}`}
            description={newsItem.shortDescription || newsItem.description}
            contentId={newsItem._id || newsItem.id}
          />
          <Link to={`/news/${newsItem.slug || newsItem.id}`} className="read-more-link">
            पूर्ण पढ़ें →
          </Link>
        </div>
      </div>
    </article>
  );
}

export default NewsCard;
